/**
 * MyLeavesPage — self-service leave apply form + balance + own history
 * (Prompt 11E.6). Replaces the 11E.5 placeholder stub.
 *
 * Reachable by any of owner/admin/manager/staff (TEACHER_ROLES) — every
 * one of those roles may have a linked staff doc and can apply for their
 * own leave. If no staff doc is linked (leave_no_staff_doc, 404) we show
 * a clear message rather than a raw error, since owners in particular may
 * legitimately have no staff record.
 */

import React, { useState } from 'react';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { PageContainer } from '@/components/layout';
import { Card, CardHeader, CardTitle, Button, Input, Dropdown, Badge } from '@/components/common';
import {
  useApplyLeave,
  useMyLeaveBalance,
  useMyLeaves,
  useLeavesConfig,
  type LeaveType,
} from '@features/attendance/api/leaves';
import {
  useMyAttendanceMonth,
  useMyOverrideRequests,
  useRequestOverride,
  type StaffDayStatus,
} from '@features/attendance/api/staffAttendance';
import type { ApiError } from '@core/api/client';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 340px;
  gap: ${({ theme }) => theme.spacing.lg};

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const BalanceRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};

  &:last-child {
    border-bottom: none;
    font-weight: ${({ theme }) => theme.typography.fontWeights.semibold};
  }
`;

const HistoryRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const statusVariant = (status: string): 'primary' | 'success' | 'error' | 'default' =>
  status === 'pending' ? 'primary' : status === 'approved' ? 'success' : 'error';

const HeatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 4px;
  margin-top: 12px;
`;

const HeatCell = styled.div<{ $status?: StaffDayStatus }>`
  aspect-ratio: 1;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  color: ${({ $status }) => ($status ? '#fff' : '#94a3b8')};
  background: ${({ $status }) =>
    $status === 'present' ? '#16a34a' : $status === 'absent' ? '#dc2626' : '#e2e8f0'};
`;

export const MyLeavesPage: React.FC = () => {
  const { data: config } = useLeavesConfig();
  const { data: balance } = useMyLeaveBalance();
  const { data: history, isLoading: historyLoading } = useMyLeaves();
  const applyLeave = useApplyLeave();

  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const { data: monthData } = useMyAttendanceMonth(year, month);
  const { data: overrideRequests } = useMyOverrideRequests();
  const requestOverride = useRequestOverride(year, month);

  const [leaveType, setLeaveType] = useState<LeaveType>('casual');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [note, setNote] = useState('');

  const [overrideDate, setOverrideDate] = useState('');
  const [overrideStatus, setOverrideStatus] = useState<StaffDayStatus>('present');
  const [overrideNote, setOverrideNote] = useState('');

  const daysByDate = new Map(monthData?.days.map((d) => [d.date, d]));
  const daysInMonth = new Date(year, month, 0).getDate();
  const firstWeekday = new Date(year, month - 1, 1).getDay();

  const handleRequestOverride = async () => {
    if (!overrideDate) {
      toast.error('Pick a date.');
      return;
    }
    try {
      await requestOverride.mutateAsync({
        date: overrideDate,
        requested_status: overrideStatus,
        note: overrideNote || undefined,
      });
      toast.success('Override request submitted — pending approval.');
      setOverrideDate('');
      setOverrideNote('');
    } catch (err) {
      toast.error((err as ApiError).message || 'Could not submit the override request.');
    }
  };

  const leaveTypeOptions = (config?.leave_types ?? ['casual', 'sick', 'other']).map((t) => ({
    value: t,
    label: t.charAt(0).toUpperCase() + t.slice(1),
  }));

  const handleSubmit = async () => {
    if (!startDate || !endDate) {
      toast.error('Pick a start and end date.');
      return;
    }
    try {
      await applyLeave.mutateAsync({
        leave_type: leaveType,
        start_date: startDate,
        end_date: endDate,
        note: note || undefined,
      });
      toast.success('Leave application submitted — pending approval.');
      setStartDate('');
      setEndDate('');
      setNote('');
    } catch (err) {
      const apiErr = err as ApiError;
      toast.error(apiErr.message || 'Could not submit leave application.');
    }
  };

  const items = history?.items ?? [];

  return (
    <PageContainer
      title="My Leaves & Attendance"
      subtitle="Apply for leave and track your balance"
      data-testid="candeza-my-leaves-page"
    >
      <Grid>
        <Card padding="lg" data-testid="candeza-leave-apply">
          <CardHeader>
            <CardTitle>Apply for leave</CardTitle>
          </CardHeader>
          <FormGrid style={{ marginTop: 16 }}>
            <Dropdown
              label="Leave type"
              options={leaveTypeOptions}
              value={leaveType}
              onChange={(v) => setLeaveType(v as LeaveType)}
              testId="candeza-leave-type"
            />
            <Input
              label="Start date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="candeza-leave-start-date"
            />
            <Input
              label="End date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="candeza-leave-end-date"
            />
          </FormGrid>
          <div style={{ marginTop: 16 }}>
            <Input
              label="Note (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={config?.note_max_len}
              data-testid="candeza-leave-note"
            />
          </div>
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              onClick={handleSubmit}
              disabled={applyLeave.isPending}
              data-testid="candeza-leave-apply-submit"
            >
              {applyLeave.isPending ? 'Submitting…' : 'Submit application'}
            </Button>
          </div>

          <CardHeader style={{ marginTop: 28 }}>
            <CardTitle>My leave history</CardTitle>
          </CardHeader>
          <div style={{ marginTop: 8 }} data-testid="candeza-leave-history">
            {historyLoading ? (
              <p style={{ color: '#64748b', fontSize: 14 }}>Loading…</p>
            ) : items.length === 0 ? (
              <p style={{ color: '#64748b', fontSize: 14 }}>No leave applications yet.</p>
            ) : (
              items.map((leave) => (
                <HistoryRow key={leave.id} data-testid={`candeza-leave-history-row-${leave.id}`}>
                  <div>
                    <strong style={{ textTransform: 'capitalize' }}>{leave.leave_type}</strong>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {leave.start_date} → {leave.end_date} · {leave.days} day
                      {leave.days === 1 ? '' : 's'}
                    </div>
                  </div>
                  <Badge variant={statusVariant(leave.status)} size="sm">
                    {leave.status}
                  </Badge>
                </HistoryRow>
              ))
            )}
          </div>
        </Card>

        <Card padding="lg" data-testid="candeza-leave-balance">
          <CardHeader>
            <CardTitle>Leave balance ({balance?.year ?? new Date().getFullYear()})</CardTitle>
          </CardHeader>
          <div style={{ marginTop: 12 }}>
            <BalanceRow>
              <span>Allocated</span>
              <span>{balance?.allocated ?? '—'}</span>
            </BalanceRow>
            <BalanceRow>
              <span>Availed</span>
              <span>{balance?.availed ?? '—'}</span>
            </BalanceRow>
            <BalanceRow>
              <span>Remaining</span>
              <span>{balance?.balance ?? '—'}</span>
            </BalanceRow>
          </div>
        </Card>
      </Grid>

      <Card padding="lg" style={{ marginTop: 24 }} data-testid="candeza-my-attendance">
        <CardHeader>
          <CardTitle>
            My attendance —{' '}
            {now.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}
          </CardTitle>
        </CardHeader>
        <HeatGrid data-testid="candeza-my-attendance-heatmap">
          {Array.from({ length: firstWeekday }).map((_, i) => (
            <div key={`pad-${i}`} />
          ))}
          {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => {
            const iso = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            const entry = daysByDate.get(iso);
            return (
              <HeatCell
                key={iso}
                $status={entry?.status}
                title={entry ? `${iso}: ${entry.status} (${entry.source})` : iso}
              >
                {day}
              </HeatCell>
            );
          })}
        </HeatGrid>

        <CardHeader style={{ marginTop: 24 }}>
          <CardTitle>Request an attendance override</CardTitle>
        </CardHeader>
        <p style={{ fontSize: 12, color: '#64748b', margin: '4px 0 12px' }}>
          Contest a day marked absent (e.g. you taught but forgot to mark attendance) — an
          owner/admin will review and decide.
        </p>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Input
            label="Date"
            type="date"
            value={overrideDate}
            onChange={(e) => setOverrideDate(e.target.value)}
            data-testid="candeza-attendance-override-date"
          />
          <Dropdown
            label="Requested status"
            options={[
              { value: 'present', label: 'Present' },
              { value: 'absent', label: 'Absent' },
            ]}
            value={overrideStatus}
            onChange={(v) => setOverrideStatus(v as StaffDayStatus)}
            testId="candeza-attendance-override-status"
          />
          <Input
            label="Note"
            value={overrideNote}
            onChange={(e) => setOverrideNote(e.target.value)}
            data-testid="candeza-attendance-override-note"
          />
          <Button
            onClick={handleRequestOverride}
            disabled={requestOverride.isPending}
            data-testid="candeza-attendance-override-submit"
          >
            Request override
          </Button>
        </div>

        <div style={{ marginTop: 20 }} data-testid="candeza-my-override-requests">
          {(overrideRequests?.items ?? []).length === 0 ? (
            <p style={{ color: '#64748b', fontSize: 14 }}>No override requests yet.</p>
          ) : (
            overrideRequests?.items.map((req) => (
              <HistoryRow key={req.id} data-testid={`candeza-override-request-row-${req.id}`}>
                <div>
                  <strong>{req.date}</strong>
                  <div style={{ fontSize: 12, color: '#64748b' }}>
                    Requested: {req.requested_status}
                    {req.note && ` · "${req.note}"`}
                  </div>
                </div>
                <Badge variant={statusVariant(req.status)} size="sm">
                  {req.status}
                </Badge>
              </HistoryRow>
            ))
          )}
        </div>
      </Card>
    </PageContainer>
  );
};

export default MyLeavesPage;
